import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { formatDateTimeBr } from '@common/app/utils/format.util';
import {
  SKIP_ERROR_HANDLER_HEADER,
  SKIP_GLOBAL_LOADING_HEADER,
} from '@common/app/constants/http.constants';
import { Observable, of, switchMap, tap, map, catchError } from 'rxjs';
import {
  AuthContext,
  buildAuthContext,
  ChangeTemporaryPasswordRequest,
  ForgotPasswordRequest,
  SignInRequest,
  StoredAuthSession,
  TokenResponse,
} from '@auth/app/core/models/auth.model';
import { TopbarProfile } from '@auth/app/core/models/token-profile.model';
import { ApplicationAccess, TenantAccess, UserAccess } from '@auth/app/core/models/user-access.model';
import { AUTH_ENV } from '@auth/app/core/tokens';
import { joinApiPath } from '@auth/app/core/utils/api-url.util';
import { getRolesFromAccessToken } from '@auth/app/core/utils/jwt-role.util';
import { extractAuthenticatedAtFromToken, extractTokenProfile } from '@auth/app/core/utils/jwt-profile.util';
import {
  flattenKnownAccessPairs,
  getModuleConfig,
} from '@auth/app/core/utils/application-registry.util';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(AUTH_ENV);

  private readonly session = signal<StoredAuthSession | null>(this.readSession());

  signIn(username: string, password: string): Observable<TokenResponse> {
    this.clearSession();

    const body: SignInRequest = {
      username: username.trim(),
      password,
    };

    return this.http
      .post<TokenResponse>(joinApiPath(this.env.url, '/auth/tenant/sign-in'), body, {
        headers: new HttpHeaders()
          .set(SKIP_GLOBAL_LOADING_HEADER, 'true')
          .set(SKIP_ERROR_HANDLER_HEADER, 'true'),
      })
      .pipe(tap((response) => this.persistTokens(response)));
  }

  /** Troca a senha temporária por uma definitiva (endpoint público, sem sessão). */
  changeTemporaryPassword(
    username: string,
    temporaryPassword: string,
    newPassword: string,
  ): Observable<void> {
    const body: ChangeTemporaryPasswordRequest = {
      username: username.trim(),
      temporary_password: temporaryPassword,
      new_password: newPassword,
    };

    return this.http.put<void>(
      joinApiPath(this.env.url, '/users/change-temporary-password'),
      body,
      {
        headers: new HttpHeaders()
          .set(SKIP_GLOBAL_LOADING_HEADER, 'true')
          .set(SKIP_ERROR_HANDLER_HEADER, 'true'),
      },
    );
  }

  /**
   * Solicita redefinição de senha (endpoint público). O eq-auth-api envia e-mail com a senha
   * temporária e um link de confirmação que redireciona para `callbackUrl?status=...`.
   */
  forgotPassword(email: string, callbackUrl?: string): Observable<void> {
    const body: ForgotPasswordRequest = {
      email: email.trim().toLowerCase(),
      ...(callbackUrl ? { callback_url: callbackUrl } : {}),
    };

    return this.http.post<void>(joinApiPath(this.env.url, '/users/forgot-password'), body, {
      headers: new HttpHeaders()
        .set(SKIP_GLOBAL_LOADING_HEADER, 'true')
        .set(SKIP_ERROR_HANDLER_HEADER, 'true'),
    });
  }

  completeSignIn(returnUrl?: string | null): Observable<string> {
    return this.loadUserAccess().pipe(
      switchMap((access) => {
        const pairs = flattenKnownAccessPairs(access, this.env.applicationModules);

        if (pairs.length === 1) {
          const applicationCode = pairs[0].application.code;
          // Aplicações externas abrem numa nova aba no clique do card; o login fica em select-access.
          if (this.isExternalApplication(applicationCode)) {
            return of(this.buildSelectAccessUrl(returnUrl));
          }

          return this.selectAccess(pairs[0].tenant, pairs[0].application).pipe(
            map(() => this.resolveDestinationAfterSelect(applicationCode, returnUrl)),
          );
        }

        return of(this.buildSelectAccessUrl(returnUrl));
      }),
    );
  }

  loadUserAccess(): Observable<UserAccess> {
    return this.http
      .get<UserAccess>(joinApiPath(this.env.url, '/users/me/access'))
      .pipe(tap((access) => this.patchSession({ userAccess: access })));
  }

  selectAccess(tenant: TenantAccess, application: ApplicationAccess): Observable<TokenResponse> {
    return this.switchApplication(tenant.id, application.id).pipe(
      tap((tokens) => {
        const context = buildAuthContext(tenant, application);
        const current = this.getValidSession();
        this.persistTokens(tokens, context, current?.userAccess);
      }),
    );
  }

  switchApplication(tenantId: string, applicationId: string): Observable<TokenResponse> {
    const headers = new HttpHeaders({
      'X-Application-ID': applicationId,
      'X-Tenant-ID': tenantId,
    });

    return this.http.post<TokenResponse>(
      joinApiPath(this.env.url, '/auth/tenant/switch'),
      null,
      { headers },
    );
  }

  logout(): void {
    const current = this.getValidSession();
    if (current?.refresh_token && current.context) {
      const headers = new HttpHeaders({
        'X-Tenant-ID': current.context.tenantId,
        'X-Application-ID': current.context.applicationId,
      });

      this.http
        .post(
          joinApiPath(this.env.url, '/auth/tenant/sign-out'),
          { refresh_token: current.refresh_token },
          { headers },
        )
        .pipe(catchError(() => of(null)))
        .subscribe(() => this.clearSession());
      return;
    }

    this.clearSession();
  }

  getAccessToken(): string | null {
    return this.getValidSession()?.access_token ?? null;
  }

  getRefreshToken(): string | null {
    return this.getValidSession()?.refresh_token ?? null;
  }

  getTokenRoles(): string[] {
    return getRolesFromAccessToken(this.getAccessToken());
  }

  getTokenProfile(): TopbarProfile {
    const session = this.getValidSession();
    const context = session?.context ?? null;
    const { displayName, systemName } = extractTokenProfile(session?.access_token ?? null, context);

    const authenticatedAt =
      session?.authenticated_at ?? extractAuthenticatedAtFromToken(session?.access_token ?? null);

    const lastAccess =
      authenticatedAt != null
        ? formatDateTimeBr(new Date(authenticatedAt).toISOString())
        : '';

    return { displayName, systemName, lastAccess };
  }

  getContext(): AuthContext | null {
    return this.getValidSession()?.context ?? null;
  }

  getUserAccess(): UserAccess | null {
    return this.getValidSession()?.userAccess ?? null;
  }

  isAuthenticated(): boolean {
    return this.getValidSession() !== null;
  }

  hasAccessContext(): boolean {
    const context = this.getContext();
    return Boolean(context?.tenantId && context?.applicationId);
  }

  isExternalApplication(applicationCode: string): boolean {
    const module = getModuleConfig(applicationCode, this.env.applicationModules);
    return Boolean(module?.externalUrl);
  }

  resolveDestinationAfterSelect(
    applicationCode: string,
    returnUrl?: string | null,
    tokens?: TokenResponse | null,
  ): string {
    const module = getModuleConfig(applicationCode, this.env.applicationModules);
    if (!module) {
      return this.buildSelectAccessUrl(returnUrl);
    }

    if (module.externalUrl) {
      return this.buildExternalRedirectUrl(module.externalUrl, tokens);
    }

    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      if (returnUrl.startsWith(module.routePrefix)) {
        return returnUrl;
      }
    }

    return module.defaultRoute;
  }

  buildSelectAccessUrl(returnUrl?: string | null): string {
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      return `${this.env.selectAccessPath}?returnUrl=${encodeURIComponent(returnUrl)}`;
    }
    return this.env.selectAccessPath;
  }

  /**
   * Monta a URL externa com os tokens do switch. Sem `tokens`, usa a sessão atual
   * (apenas fallback; o fluxo de select-access deve passar a resposta do switch).
   */
  private buildExternalRedirectUrl(baseUrl: string, tokens?: TokenResponse | null): string {
    const { accessToken, refreshToken } = this.resolveRedirectTokens(tokens);
    const url = new URL(baseUrl);
    url.searchParams.set('accessToken', accessToken);
    url.searchParams.set('refreshToken', refreshToken);
    return url.toString();
  }

  private resolveRedirectTokens(tokens?: TokenResponse | null): {
    accessToken: string;
    refreshToken: string;
  } {
    if (tokens) {
      const raw = tokens as TokenResponse & Record<string, unknown>;
      return {
        accessToken: String(raw.access_token ?? raw['accessToken'] ?? ''),
        refreshToken: String(raw.refresh_token ?? raw['refreshToken'] ?? ''),
      };
    }

    return {
      accessToken: this.getAccessToken() ?? '',
      refreshToken: this.getRefreshToken() ?? '',
    };
  }

  private persistTokens(
    response: TokenResponse,
    context?: AuthContext,
    userAccess?: UserAccess,
  ): void {
    const raw = response as TokenResponse & Record<string, unknown>;
    const access_token = raw.access_token ?? (raw['accessToken'] as string | undefined);
    const refresh_token = raw.refresh_token ?? (raw['refreshToken'] as string | undefined);
    const expires_in = Number(raw.expires_in ?? raw['expiresIn'] ?? 3600);

    if (!access_token) {
      return;
    }

    const current = this.getValidSession();
    const stored: StoredAuthSession = {
      access_token,
      // Switch/token-exchange às vezes devolve só access_token; não apagar o refresh da sessão.
      refresh_token: refresh_token || current?.refresh_token || '',
      expires_in,
      token_type: raw.token_type ?? (raw['tokenType'] as string | undefined) ?? 'bearer',
      refresh_expires_in:
        raw.refresh_expires_in ?? (raw['refreshExpiresIn'] as number | undefined),
      scope: raw.scope ?? (raw['scope'] as string | undefined),
      expires_at: Date.now() + expires_in * 1000,
      authenticated_at: Date.now(),
      context: context ?? current?.context,
      userAccess: userAccess ?? current?.userAccess,
    };

    localStorage.setItem(this.env.storageKey, JSON.stringify(stored));
    this.session.set(stored);
  }

  private patchSession(patch: Partial<StoredAuthSession>): void {
    const current = this.getValidSession();
    if (!current) {
      return;
    }
    const stored = { ...current, ...patch };
    localStorage.setItem(this.env.storageKey, JSON.stringify(stored));
    this.session.set(stored);
  }

  private clearSession(): void {
    localStorage.removeItem(this.env.storageKey);
    this.session.set(null);
  }

  private getValidSession(): StoredAuthSession | null {
    const current = this.session() ?? this.readSession();
    if (!current?.access_token) {
      return null;
    }

    if (current.expires_at && Date.now() > current.expires_at) {
      this.clearSession();
      return null;
    }

    return this.ensureAuthenticatedAt(current);
  }

  private ensureAuthenticatedAt(session: StoredAuthSession): StoredAuthSession {
    if (session.authenticated_at != null) {
      return session;
    }

    const authenticatedAt = extractAuthenticatedAtFromToken(session.access_token);
    if (authenticatedAt == null) {
      return session;
    }

    const updated: StoredAuthSession = { ...session, authenticated_at: authenticatedAt };
    localStorage.setItem(this.env.storageKey, JSON.stringify(updated));
    this.session.set(updated);
    return updated;
  }

  private readSession(): StoredAuthSession | null {
    try {
      const raw = localStorage.getItem(this.env.storageKey);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as StoredAuthSession;
    } catch {
      localStorage.removeItem(this.env.storageKey);
      return null;
    }
  }
}
